/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { type AxiosInstance, type AxiosError } from 'axios';
import { type AcesDealsVehicleAttributes, type DealData, type DealResponse } from '../interface';
import { requestBaseURL } from '@/config/serviceAPI';
import { isAxiosError } from '@/utils/errorsHandling';
import { createQuery } from '@/utils/createReactQuery';
import { useQueryResultOnError } from '@/hooks/query/useQueryResultOnError';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useAcesDealsVehicleAttributes } from '@/features/deals/hooks/useAcesDealsVehicleAttributes';

type ApiVersion = 'v3' | 'v4';

type OptionsV3 = {
  apiVersion: 'v3';
  skuId: string;
  dealId: string;
  fulfillmentOption: string;
};

type OptionsV4 = {
  apiVersion: 'v4';
  skuId: string;
  dealId: string;
  fulfillmentOption: string;
  dealsVehicleAttributes: AcesDealsVehicleAttributes | null;
};

type Options = OptionsV3 | OptionsV4;

const urlByVersion: Record<ApiVersion, string> = {
  v3: `${requestBaseURL}/ecomm/b2c/browse/v3/skus`,
  v4: `${requestBaseURL}/ecomm/b2c/browse/v4/skus`,
};

export const getDeal = async (options: Options, axiosInstance?: AxiosInstance) => {
  const { skuId, dealId, fulfillmentOption, apiVersion } = options;
  const baseUrl = urlByVersion[apiVersion];
  const params =
    options.apiVersion === 'v4' && options.dealsVehicleAttributes
      ? { ...options.dealsVehicleAttributes }
      : undefined;

  const response = await getAxios(axiosInstance).get<DealResponse>(
    `${baseUrl}/${skuId}/deals/${dealId}/fulfillment-option/${fulfillmentOption}`,
    {
      params,
    },
  );

  return selector(response.data);
};

const selector = (data: DealResponse): DealData => data;

const { useData } = createQuery<DealData, Options>('dealDetails', getDeal);

type PublicOptions = Omit<Options, 'apiVersion' | 'dealsVehicleAttributes'> & {
  enabled?: boolean;
  onError?: (err: AxiosError) => void;
};

export const useDeal = ({ enabled = true, onError, ...rest }: PublicOptions) => {
  const { acesEnabled } = useAcesFlags();
  const dealsVehicleAttributes = useAcesDealsVehicleAttributes();

  const options = acesEnabled
    ? {
        apiVersion: 'v4' as const,
        dealsVehicleAttributes,
        enabled,
        ...rest,
      }
    : {
        apiVersion: 'v3' as const,
        enabled,
        ...rest,
      };

  const queryResult = useData({
    ...options,
  });

  useQueryResultOnError(queryResult, (error) => {
    if (onError && isAxiosError(error)) {
      onError(error);
    }
  });

  return queryResult;
};
